import {
    Component,
    computed,
    effect,
    input,
    output,
    signal,
    viewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { AutoComplete } from '@openng/optimus-ui/autocomplete';
import { TranslatePipe } from '@ngx-translate/core';
import { NgTemplateOutlet } from '@angular/common';
import { sleep } from '@libs/shared/utils/sleep';
import { BaseInputComponentV2 } from '../base-input.component-v2';
import { FloatLabelComponent } from '../../float-label/float-label.component';
import { InputError } from '../input-error/input-error.component';

@Component({
    selector: 'app-tags-input',
    templateUrl: 'tags-input.component.html',
    styleUrl: 'tags-input.component.scss',
    imports: [
        AutoComplete,
        FormsModule,
        TranslatePipe,
        FloatLabelComponent,
        NgTemplateOutlet,
        InputError,
    ],
})
export class TagsInputComponent extends BaseInputComponentV2<string[]> {

    public suggestions = input<string[], string[] | null | undefined>([], {
        transform: (value) => value ?? [],
    });
    public methodForFilteringSuggestions = input<'startsWith' | 'includes'>(
        'startsWith',
    );
    public showClear = input<boolean>(true);
    public placeholder = input<string>('');
    public separator = input<string>(',');
    public submitOnBlur = input<boolean>(false);
    public queryEvent = output<string>();

    protected inputComponent = viewChild(AutoComplete);
    protected autocompletePlaceholder = computed(() =>
        this.showLabel() ? undefined : this.placeholder(),
    );
    protected filteredSuggestions = computed(() => {
        const searchText = this.currentQuery().toLowerCase();
        const selectedValues = this.value() ?? [];
        const matches =
            this.methodForFilteringSuggestions() === 'startsWith'
                ? (item: string) => item.toLowerCase().startsWith(searchText)
                : (item: string) => item.toLowerCase().includes(searchText);

        return this.suggestions().filter(
            (item) => matches(item) && !selectedValues.includes(item),
        );
    });

    private currentQuery = signal<string>('');
    protected readonly hasContent = computed(() => Boolean(this.value()?.length || this.currentQuery().length));

    constructor() {
        super();

        effect(() => {
            const component = this.inputComponent();
            const searchText = this.currentQuery();
            const suggestions = this.filteredSuggestions();

            if (!component || !searchText.length || !suggestions.length) return;

            setTimeout(() => component.show());
        });
    }

    protected queryChange(event: { query?: string }): void {
        const query = event.query ?? '';
        this.currentQuery.set(query);
        this.queryEvent.emit(query);
    }

    protected inputChanged(event: Event): void {
        if (event.target instanceof HTMLInputElement) {
            this.currentQuery.set(event.target.value);
        }
    }

    protected submitOnEnter(event: KeyboardEvent): void {
        if (event.code === 'Enter' || event.code === 'NumpadEnter')
            void this.submit();
    }

    protected async blur(): Promise<void> {
        await this.submit(true);
        this.touch.emit();
    }

    protected selectionChanged(): void {
        this.currentQuery.set('');
        this.value.set(this.value()?.length ? [...this.value()] : []);
    }

    private async submit(onBlur = false): Promise<void> {
        if (onBlur && !this.submitOnBlur()) return;
        if (onBlur && this.filteredSuggestions().length) await sleep(200);

        const query = this.currentQuery();
        if (!query.length) return;

        const newValues = query
            .split(this.separator())
            .map((item) => item.trim())
            .filter(Boolean);
        this.value.set(
            Array.from(new Set([...(this.value() ?? []), ...newValues])),
        );
        this.currentQuery.set('');
        const input = this.inputComponent()?.multiInputEl?.nativeElement;
        if (input) input.value = '';
    }
}
